import { supabase, SUPABASE_BUCKET } from "./supabase";

/* ===== Public reads ===== */
export const listCourses = async () => {
  const { data, error } = await supabase.from("courses").select("*").order("name");
  if (error) throw error;
  return data || [];
};

export const listPlayers = async () => {
  const { data, error } = await supabase.from("players").select("*").order("shirt_number");
  if (error) throw error;
  return data || [];
};

export const getPlayerStats = async (personaId) => {
  if (!personaId) return [];
  const { data, error } = await supabase
    .from("statistiche_partita")
    .select("*, partita:partite(data, avversario, casa_trasferta, tipo, gol_fatti, gol_subiti)")
    .eq("persona_id", personaId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
};

export const getPlayerProfile = async (personaId) => {
  if (!personaId) return null;
  const { data, error } = await supabase
    .from("public_player_profiles")
    .select("*")
    .eq("id", personaId)
    .maybeSingle();
  if (error) throw error;
  return data || null;
};

export const listGiocatoriGestionale = async () => {
  const { data, error } = await supabase
    .from("public_giocatori_selezionabili")
    .select("*");
  if (error) throw error;
  return data || [];
};

export const listGiocatoriPersone = async () => {
  const { data, error } = await supabase
    .from("giocatori_gestionale")
    .select("*")
    .order("cognome");
  if (error) throw error;
  return data || [];
};

export const listStaff = async () => {
  const { data, error } = await supabase.from("staff").select("*").order("created_at");
  if (error) throw error;
  return data || [];
};

export const listMatches = async () => {
  const { data, error } = await supabase
    .from("matches")
    .select("*")
    .order("match_date", { ascending: true });
  if (error) throw error;
  return (data || []).map((m) => ({ ...m, date: m.match_date }));
};

export const listNews = async () => {
  const { data, error } = await supabase
    .from("news")
    .select("*")
    .order("published_at", { ascending: false });
  if (error) throw error;
  return data || [];
};

export const getSiteSetting = async (key) => {
  const { data, error } = await supabase
    .from("site_settings")
    .select("value")
    .eq("key", key)
    .maybeSingle();
  if (error) throw error;
  return data?.value || null;
};

export const getSiteImages = async () => {
  const { data, error } = await supabase.from("site_images").select("*");
  if (error) return {};
  return Object.fromEntries((data || []).map((r) => [r.key, r.public_url]));
};

export const listSponsors = async () => {
  const today = new Date().toISOString().split("T")[0];
  const { data, error } = await supabase
    .from("sponsors")
    .select("*")
    .eq("active", true)
    .or(`data_scadenza.is.null,data_scadenza.gte.${today}`)
    .order("sort_order", { ascending: true });
  if (error) throw error;
  return data || [];
};

/* ===== Public writes ===== */
export const sendContact = async (payload) => {
  const { error } = await supabase.from("contact_messages").insert(payload);
  if (error) throw error;
};

/* ===== Authenticated ===== */
export const createEnrollment = async (userId, course, payload) => {
  const row = {
    user_id: userId || null,
    course_id: course.id,
    course_name: course.name,
    full_name: payload.full_name,
    email: payload.email,
    phone: payload.phone,
    notes: payload.notes || null,
  };
  const { data, error } = await supabase.from("enrollments").insert(row).select().single();
  if (error) throw error;
  return data;
};

export const listMyEnrollments = async (userId) => {
  const { data, error } = await supabase
    .from("enrollments")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
};

/* ===== Admin: storage ===== */
export const adminListFiles = async () => {
  const { data, error } = await supabase
    .from("site_files")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
};

export const adminUpdateFileFolder = async (fileId, folder) => {
  const { error } = await supabase.from("site_files").update({ folder }).eq("id", fileId);
  if (error) throw error;
};

export const adminUploadFile = async (file, label, folder) => {
  const ext = file.name.includes(".") ? file.name.split(".").pop() : "bin";
  const filename = `${crypto.randomUUID().replace(/-/g, "")}.${ext}`;
  const { error: upErr } = await supabase.storage
    .from(SUPABASE_BUCKET)
    .upload(filename, file, { contentType: file.type, cacheControl: "3600", upsert: false });
  if (upErr) throw upErr;
  const { data: pub } = supabase.storage.from(SUPABASE_BUCKET).getPublicUrl(filename);
  const public_url = pub.publicUrl;
  const { data, error } = await supabase
    .from("site_files")
    .insert({
      filename,
      original_filename: file.name,
      label: label || file.name,
      public_url,
      content_type: file.type,
      size: file.size,
      folder: folder || "Generale",
    })
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const adminDeleteFile = async (fileRow) => {
  await supabase
    .from("site_images")
    .update({ public_url: null, file_id: null })
    .eq("file_id", fileRow.id);
  await supabase.storage.from(SUPABASE_BUCKET).remove([fileRow.filename]);
  const { error } = await supabase.from("site_files").delete().eq("id", fileRow.id);
  if (error) throw error;
};

/* ===== Admin: site image slots ===== */
const DEFAULT_SLOTS = [
  { key: "home_hero", label: "Home – Hero background", default_url: "https://images.pexels.com/photos/11097680/pexels-photo-11097680.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600" },
  { key: "home_card_team", label: "Home – Card squadra", default_url: "https://images.unsplash.com/photo-1522778526097-ce0a22ceb253?w=1200&q=80" },
  { key: "squadra_hero", label: "Squadra – Hero", default_url: "https://images.unsplash.com/photo-1518609878373-06d740f60d8b?w=1600&q=80" },
  { key: "palestra_hero", label: "Palestra – Hero", default_url: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1600&q=80" },
  { key: "chisiamo_hero", label: "Chi siamo – Hero", default_url: "https://images.unsplash.com/photo-1551958219-acbc608c6377?w=1600&q=80" },
  { key: "chisiamo_main", label: "Chi siamo – Foto principale", default_url: "https://images.unsplash.com/photo-1517466787929-bc90951d0974?w=900&q=80" },
];

export const adminListSlots = async () => {
  const { data } = await supabase.from("site_images").select("*");
  const byKey = Object.fromEntries((data || []).map((r) => [r.key, r]));
  return DEFAULT_SLOTS.map((s) => ({
    ...s,
    public_url: byKey[s.key]?.public_url || null,
    file_id: byKey[s.key]?.file_id || null,
  }));
};

export const adminAssignSlot = async (key, file) => {
  const update = file
    ? { key, public_url: file.public_url, file_id: file.id }
    : { key, public_url: null, file_id: null };
  const { error } = await supabase.from("site_images").upsert(update, { onConflict: "key" });
  if (error) throw error;
};

export const resolveSiteImages = async () => {
  const slots = await adminListSlots();
  return Object.fromEntries(slots.map((s) => [s.key, s.public_url || s.default_url]));
};

/* ===== Admin: CRUD ===== */
const crud = (table) => ({
  list: async () => {
    const { data, error } = await supabase.from(table).select("*").order("created_at", { ascending: false });
    if (error) throw error;
    return data || [];
  },
  create: async (payload) => {
    const { data, error } = await supabase.from(table).insert(payload).select().single();
    if (error) throw error;
    return data;
  },
  update: async (id, payload) => {
    const { data, error } = await supabase.from(table).update(payload).eq("id", id).select().single();
    if (error) throw error;
    return data;
  },
  remove: async (id) => {
    const { error } = await supabase.from(table).delete().eq("id", id);
    if (error) throw error;
  },
});

export const coursesCrud = crud("courses");
export const playersCrud = crud("players");
export const staffCrud = crud("staff");
export const matchesCrud = crud("matches");
export const newsCrud = crud("news");
export const sponsorsCrud = crud("sponsors");

export const adminListEnrollments = async () => {
  const { data, error } = await supabase
    .from("enrollments")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
};

export const adminListMessages = async () => {
  const { data, error } = await supabase
    .from("contact_messages")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
};

export const getSiteSettings = async () => {
  const { data, error } = await supabase.from("site_settings").select("*");
  if (error) throw error;
  return Object.fromEntries((data || []).map((r) => [r.key, r.value]));
};

export const updateSiteSetting = async (key, value) => {
  const { data, error } = await supabase
    .from("site_settings")
    .upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: "key" })
    .select();
  if (error) throw error;
};

export const listGymEvents = async () => {
  const { data, error } = await supabase
    .from("gym_events")
    .select("*")
    .eq("active", true)
    .order("day_order", { ascending: true })
    .order("time_start", { ascending: true });
  if (error) throw error;
  return data || [];
};

export const gymEventsCrud = crud("gym_events");

export const listGymSospensioni = async () => {
  const { data, error } = await supabase
    .from("gym_sospensioni")
    .select("*")
    .order("date", { ascending: true });
  if (error) throw error;
  return data || [];
};

export const gymSospensioniCrud = crud("gym_sospensioni");

export const listGenericEvents = async () => {
  const { data, error } = await supabase
    .from("eventi_generici")
    .select("*")
    .eq("active", true)
    .order("event_date", { ascending: true });
  if (error) throw error;
  return data || [];
};

export const genericEventsCrud = crud("eventi_generici");

/* ===== Ritiri estivi ===== */
export const listRitiri = async () => {
  const { data, error } = await supabase
    .from("ritiri")
    .select("*")
    .eq("active", true)
    .order("start_date", { ascending: true });
  if (error) throw error;
  return data || [];
};

export const ritiriCrud = crud("ritiri");

export const createPrenotazioneRitiro = async (ritiroId, payload) => {
  const row = {
    ritiro_id: ritiroId,
    full_name: payload.full_name,
    email: payload.email,
    phone: payload.phone,
    ragazzo_nome: payload.ragazzo_nome || null,
    num_partecipanti: payload.num_partecipanti || 1,
    notes: payload.notes || null,
  };
  const { data, error } = await supabase.from("ritiri_prenotazioni").insert(row).select().single();
  if (error) throw error;
  return data;
};

export const adminListPrenotazioniRitiri = async () => {
  const { data, error } = await supabase
    .from("ritiri_prenotazioni")
    .select("*, ritiro:ritiri(title, start_date, end_date)")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
};

export const adminUpdatePrenotazioneRitiroStatus = async (id, status) => {
  const { error } = await supabase.from("ritiri_prenotazioni").update({ status }).eq("id", id);
  if (error) throw error;
};

export const adminDeletePrenotazioneRitiro = async (id) => {
  const { error } = await supabase.from("ritiri_prenotazioni").delete().eq("id", id);
  if (error) throw error;
};

export const adminListSiteUsers = async () => {
  // Prendi gli user_id che hanno un ruolo nel gestionale
  const { data: gestionaleUsers } = await supabase
    .from("user_roles")
    .select("user_id");

  const gestionaleIds = (gestionaleUsers || []).map((r) => r.user_id);

  // Prendi i profili che NON sono nel gestionale e non sono admin
  const { data, error } = await supabase
    .from("profiles")
    .select("id, email, name, created_at, role")
    .not("role", "eq", "admin")
    .order("created_at", { ascending: false });

  if (error) throw error;

  // Filtra lato client escludendo chi è nel gestionale
  return (data || []).filter((u) => !gestionaleIds.includes(u.id));
};